import React, { useMemo, useState } from 'react';
import {
  Button,
  CustomFilter,
  DatePicker,
  EllipsisSpan,
  Form,
  Input,
  Modal,
  Paging,
  Select,
  Table,
  Tag,
  Wrapper,
  message,
  useBreadcrumbRoutes,
  usePaging,
  useUpdate,
} from '@maxtropy/components';
import { Space } from 'antd';
import { useRequest } from 'ahooks';
import dayjs, { Dayjs } from 'dayjs';
import {
  apiV2ExportCenterTaskBatchDeletePost,
  apiV2ExportCenterTaskBatchDownloadPost,
  apiV2ExportCenterTaskDeletePost,
  apiV2ExportCenterTaskDownloadPost,
  apiV2ExportCenterTaskPagePost,
  V2ExportCenterTaskPagePostResponse,
} from '@maxtropy/device-customer-apis-v2';
import styles from './index.module.scss';

enum ReportExportStatus {
  PROCESSING = 0,
  SUCCESS = 1,
  FAILED = 2,
}

type ExportTaskResponseItem = NonNullable<V2ExportCenterTaskPagePostResponse['list']>[number];

interface FilterValues {
  fileName?: string;
  status?: ReportExportStatus;
  exportDate?: [Dayjs, Dayjs];
}

interface SearchParams {
  fileName?: string;
  status?: ReportExportStatus;
  startDate?: string;
  endDate?: string;
}

const statusConfig = {
  [ReportExportStatus.PROCESSING]: { text: '进行中', color: 'default' },
  [ReportExportStatus.SUCCESS]: { text: '成功', color: 'success' },
  [ReportExportStatus.FAILED]: { text: '失败', color: 'error' },
};

const statusOptions = Object.entries(statusConfig).map(([value, config]) => ({
  value: Number(value),
  label: config.text,
}));

const getBatchFileName = () => `报表下载${dayjs().format('YYYYMMDDHHmmss')}.zip`;

const saveDownloadResponse = (response: unknown, fallbackName: string) => {
  const result = response as { url?: string; fileUrl?: string; downloadUrl?: string; data?: Blob } | Blob | string;
  const url =
    typeof result === 'string'
      ? result
      : result instanceof Blob
      ? window.URL.createObjectURL(result)
      : result.url ||
        result.fileUrl ||
        result.downloadUrl ||
        (result.data ? window.URL.createObjectURL(result.data) : '');
  if (!url) return;
  const link = document.createElement('a');
  link.href = url;
  link.download = fallbackName;
  link.click();
  if (url.startsWith('blob:')) window.URL.revokeObjectURL(url);
};

const ReportExport: React.FC = () => {
  const breadcrumbRoutes = useBreadcrumbRoutes();
  const [form] = Form.useForm<FilterValues>();
  const pagingInfo = usePaging(50);
  const { pageOffset, pageSize, setPageOffset, setTotalCount } = pagingInfo;
  const [searchParams, setSearchParams] = useState<SearchParams>({});
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [selectedRows, setSelectedRows] = useState<ExportTaskResponseItem[]>([]);
  const [updateState, update] = useUpdate();

  const { data = [], loading } = useRequest(
    () =>
      apiV2ExportCenterTaskPagePost({
        timestamp: Date.now(),
        page: pageOffset,
        size: pageSize,
        fileName: searchParams.fileName,
        startDate: searchParams.startDate,
        endDate: searchParams.endDate,
        status: searchParams.status,
      }).then(res => {
        setTotalCount(res.total || 0);
        return res.list || [];
      }),
    { refreshDeps: [pageOffset, pageSize, searchParams, updateState] }
  );

  const handleDelete = (records: ExportTaskResponseItem[]) => {
    if (!records.length) {
      message.warning('请先选择要删除的报表');
      return;
    }
    Modal.confirm({
      title: '确认删除',
      content: `确定删除选中的${records.length}个报表吗？删除后不可恢复。`,
      onOk: async () => {
        const taskIds = records.map(item => item.id!);
        if (taskIds.length === 1) {
          await apiV2ExportCenterTaskDeletePost({ taskId: taskIds[0] });
        } else {
          await apiV2ExportCenterTaskBatchDeletePost({ taskIds });
        }
        message.success('删除成功');
        setSelectedRowKeys([]);
        setSelectedRows([]);
        update();
      },
    });
  };

  const handleDownload = async (records: ExportTaskResponseItem[]) => {
    if (!records.length) {
      message.warning('请先选择要下载的报表');
      return;
    }
    if (records.length > 100) {
      message.warning('最多选择100个报表下载');
      return;
    }
    if (records.some(item => item.status !== ReportExportStatus.SUCCESS)) {
      message.warning('仅导出成功的报表可以下载');
      return;
    }
    const fallbackName = records.length > 1 ? getBatchFileName() : records[0].fileName || '报表.xlsx';
    try {
      const taskIds = records.map(item => item.id!);
      const response =
        taskIds.length === 1
          ? await apiV2ExportCenterTaskDownloadPost({ taskId: taskIds[0] })
          : await apiV2ExportCenterTaskBatchDownloadPost({ taskIds });
      saveDownloadResponse(response, fallbackName);
    } catch {
      message.error('下载失败，请稍后重试');
    }
  };

  const columns = useMemo(
    () => [
      {
        title: '报表名称',
        dataIndex: 'fileName',
        width: 260,
        render: (value: string) => <EllipsisSpan value={value || '--'} />,
      },
      {
        title: '状态',
        dataIndex: 'status',
        width: 100,
        render: (status: ReportExportStatus) => {
          const config = statusConfig[status];
          return config ? (
            <Tag border="solid" color={config.color}>
              {config.text}
            </Tag>
          ) : (
            '--'
          );
        },
      },
      {
        title: '导出日期',
        dataIndex: 'createTime',
        width: 180,
        render: (value: string | number) => (value ? dayjs(value).format('YYYY-MM-DD HH:mm:ss') : '--'),
      },
      {
        title: '导出人',
        dataIndex: 'createByUsername',
        width: 140,
        render: (value: string) => <EllipsisSpan value={value || '--'} />,
      },
      {
        title: '表格所在功能模块',
        dataIndex: 'exportBusinessTypeName',
        width: 220,
        render: (value: string) => <EllipsisSpan value={value || '--'} />,
      },
      {
        title: '操作',
        key: 'action',
        fixed: 'right' as const,
        width: 180,
        render: (_: unknown, record: ExportTaskResponseItem) => (
          <Space size="small">
            <Button
              type="link"
              danger
              disabled={record.status === ReportExportStatus.PROCESSING}
              onClick={() => handleDelete([record])}
            >
              删除
            </Button>
            <Button
              type="link"
              disabled={record.status !== ReportExportStatus.SUCCESS}
              onClick={() => handleDownload([record])}
            >
              下载
            </Button>
            {record.status === ReportExportStatus.FAILED && (
              <Button
                type="link"
                onClick={() =>
                  Modal.info({ title: '失败原因', content: record.errorMessage || '暂无失败原因', okText: '知道了' })
                }
              >
                原因
              </Button>
            )}
          </Space>
        ),
      },
    ],
    []
  );

  const filters = (
    <CustomFilter<FilterValues>
      form={form}
      onFinish={values => {
        setPageOffset(1);
        setSearchParams({
          fileName: values.fileName?.trim() || undefined,
          status: values.status,
          startDate: values.exportDate?.[0].format('YYYY-MM-DD'),
          endDate: values.exportDate?.[1].format('YYYY-MM-DD'),
        });
      }}
      onReset={() => {
        setPageOffset(1);
        setSearchParams({});
      }}
    >
      <Form.Item name="fileName" label="报表名称">
        <Input allowClear placeholder="请输入" />
      </Form.Item>
      <Form.Item name="status" label="状态">
        <Select allowClear placeholder="请选择" options={statusOptions} />
      </Form.Item>
      <Form.Item name="exportDate" label="导出日期">
        <DatePicker.RangePicker format="YYYY-MM-DD" />
      </Form.Item>
    </CustomFilter>
  );

  return (
    <Wrapper routes={breadcrumbRoutes?.routes} filters={filters} className={styles.wrapper}>
      <div className={styles.content}>
        <Space size="small" className={styles.actions}>
          <Button type="primary" disabled={!selectedRows.length} onClick={() => handleDownload(selectedRows)}>
            下载
          </Button>
          <Button type="primary" disabled={!selectedRows.length} onClick={() => handleDelete(selectedRows)}>
            删除
          </Button>
        </Space>
        <div className={styles.tableArea}>
          <Table
            rowKey="id"
            sticky
            loading={loading}
            columns={columns}
            dataSource={data}
            scroll={{ x: 1100 }}
            rowSelection={{
              selectedRowKeys,
              preserveSelectedRowKeys: true,
              getCheckboxProps: record => ({ disabled: record.status === ReportExportStatus.PROCESSING }),
              onChange: (keys, rows) => {
                setSelectedRowKeys(keys);
                setSelectedRows(previousRows => {
                  const rowMap = new Map(previousRows.map(row => [row.id, row]));
                  rows.forEach(row => rowMap.set(row.id, row));
                  return keys.map(key => rowMap.get(key)).filter((row): row is ExportTaskResponseItem => Boolean(row));
                });
              },
            }}
          />
        </div>
        <div className={styles.pagination}>
          <Paging pagingInfo={pagingInfo} pageSizeOptions={['10', '20', '50', '100', '200', '500']} />
        </div>
      </div>
    </Wrapper>
  );
};

export default ReportExport;
